import type { StarterSyncReport } from '@racemap/sdk/schema/starters';
import { capitalize } from '@racemap/utilities/functions/utils';
import { Card, Collapse, Flex, Modal, Space, Tag, Typography } from 'antd';
import { type FC, useState } from 'react';
import { toast } from 'src/lib/toast';
import { useEventEditorActions, useLastStarterSyncReport } from 'src/store/events/events_reducers';

interface Props {
  open: boolean;
  onCancel?: () => void;
}

const { Title, Text } = Typography;

export const SyncParticipantsReportModal: FC<Props> = ({ open, onCancel }) => {
  const lastSyncReport = useLastStarterSyncReport();
  const [isSyncing, setIsSyncing] = useState(false);
  const { syncStartersOfEvent } = useEventEditorActions();

  if (lastSyncReport == null) return null;

  const handleRetry = async () => {
    try {
      setIsSyncing(true);
      await syncStartersOfEvent({ showToast: false });
    } catch (error) {
      toast.error('Error syncing starters. Please try again later.');
      console.error(error);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      okText="Retry"
      okButtonProps={{ loading: isSyncing }}
      onOk={handleRetry}
      width={600}
    >
      <Flex vertical>
        <Title level={4}>Participant Sync Report</Title>
        <Space>
          <Text strong>Status: </Text>
          <Tag color={getStatusColor(lastSyncReport.status)}>
            {capitalize(lastSyncReport.status)}
          </Tag>
        </Space>
        {lastSyncReport.counts != null && (
          <>
            <Space>
              <Text strong>Created: </Text>
              <Text>{lastSyncReport.counts.created}</Text>|<Text strong>Updated: </Text>
              <Text>{lastSyncReport.counts.updated}</Text>|<Text strong>Not Changed: </Text>
              <Text>{lastSyncReport.counts.notChanged}</Text>|<Text strong>Skipped: </Text>
              <Text>{lastSyncReport.counts.skipped}</Text>
            </Space>
            <Space style={{ marginBottom: 16 }}>
              <Text strong>Marked Abandoned: </Text>
              <Text>{lastSyncReport.counts.markedAbandoned}</Text>|<Text strong>Deleted: </Text>
              <Text>{lastSyncReport.counts.deleted}</Text>
            </Space>
          </>
        )}

        <Text strong style={{ marginBottom: 8 }}>
          Errors:
        </Text>
        {lastSyncReport.errors.length === 0 && <Text>No errors</Text>}
        <Space
          orientation="vertical"
          style={{ width: '100%', maxHeight: '50vh', overflowY: 'auto' }}
        >
          {lastSyncReport.errors.map((error, index) => (
            <ErrorCard key={error.id} error={error} index={index} />
          ))}
        </Space>
      </Flex>
    </Modal>
  );
};

const ErrorCard = ({
  error,
  index,
}: { error: StarterSyncReport['errors'][number]; index: number }) => (
  <Card size="small">
    <Space orientation="vertical">
      <Text strong>
        {index + 1}. {error.name}
      </Text>
      <Space orientation="vertical" size={2}>
        <TextEntry label="Type" value={error.errorType} />
        <TextEntry label="Import Key" value={error.importKey} />
        <TextEntry label="Import ID" value={error.importId} />
        <TextEntry label="Starter Key" value={error.starterKey} />
        <TextEntry label="Source URL" value={error.sourceUrl} />
        <MessageEntry value={error.message} />
      </Space>
      {error.record && (
        <Collapse
          size="small"
          items={[
            {
              key: 'record',
              label: 'Record',
              children: (
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 8,
                    borderRadius: 4,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(error.record, null, 2)}
                </pre>
              ),
            },
          ]}
        />
      )}
    </Space>
  </Card>
);

const TextEntry = ({ label, value }: { label: string; value?: string }) =>
  value ? (
    <Text>
      <Text strong>{label}: </Text>
      {value}
    </Text>
  ) : null;

const MessageEntry = ({ value }: { value: string }) => (
  <Text>
    <Text strong>Message: </Text>
    <pre>{value}</pre>
  </Text>
);

const getStatusColor = (status: StarterSyncReport['status']) => {
  switch (status) {
    case 'success':
      return 'green';
    case 'partial':
      return 'orange';
    case 'error':
      return 'red';
    default:
      return 'blue';
  }
};
